import { useLocale } from '../../context/LocaleContext';
import { usePersonalisationContext } from '../../context/PersonalisationContext';
import { calculateFinalPriceModifier } from '../../utilities/priceModifier';
import { getFontDisplayName } from '../../utilities/getFontDisplayName';

/**
 * Custom hook for PersonalisationSummaryModal logic and className selection.
 * @param {Object} props - Props for the view model.
 * @param {function} [props.onClose] - Callback for closing the modal.
 * @returns {Object} View model for the summary modal component.
 */
export function useViewModel({ onClose } = {}) {
  const {
    product,
    personalisationSteps,
    personalisationSlots,
    selections,
    variants,
    componentGroups,
    currentTotalPrice,
    setCurrentStepId
  } = usePersonalisationContext();
  const { formatPrice } = useLocale();

  /**
   * Finds which step a slot belongs to.
   * @param {string} slotId - The ID of the slot to find the step for.
   * @returns {string|null} - The step ID that contains the slot, or null if not found.
   */
  const findStepForSlot = (slotId) => {
    if (!personalisationSteps || !slotId) return null;

    for (const [stepId, step] of personalisationSteps.entries()) {
      if (step.slots && step.slots.includes(slotId)) {
        return stepId;
      }
    }
    return null;
  };

  /**
   * Gets the display value for a slot selection.
   * @param {Object} slot - The slot object.
   * @param {string|string[]|Object} selectedValue - The selected value(s).
   * @returns {string} - The display value for the selection.
   */
  const getSlotDisplayValue = (slot, selectedValue) => {
    if (!selectedValue) return '';

    // Handle file uploads - check if selectedValue is a File object.
    if (selectedValue instanceof File) {
      return selectedValue.name;
    }

    // Handle alphanumeric slots with font selection (object format)
    if (slot.slotType === 'ALPHANUMERIC' && typeof selectedValue === 'object' && selectedValue.text) {
      let displayText = selectedValue.text;

      // If translation is enabled and we have translated text, show both values
      if (
        slot.translateToLanguage &&
        selectedValue.translatedText &&
        selectedValue.translatedText !== selectedValue.text
      ) {
        displayText = `${selectedValue.text} / ${selectedValue.translatedText}`;
      }

      // Show text and font if font selection is available.
      if (slot.allowedFonts && slot.allowedFonts.length > 0 && selectedValue.font) {
        return `${displayText}<br>(${getFontDisplayName({ fontName: selectedValue.font })})`;
      }
      return displayText;
    }

    if (slot.slotType === 'CHECKBOX') {
      // Handle checkbox slots - show custom text if available, otherwise show default
      return slot.checkboxSummaryValue || 'Accepted';
    }

    if (slot.slotType === 'COMPONENT' || slot.slotType === 'BASE_CONFIG') {
      // Handle arrays for upsells
      if (Array.isArray(selectedValue)) {
        const variantTitles = selectedValue
          .map((variantId) => {
            const variant = variants.get(variantId);

            // Get component group info if available
            let groupPrefix = '';
            if (variant?.componentGroupId) {
              const componentGroup = componentGroups.get(variant.componentGroupId);
              if (componentGroup) {
                groupPrefix = componentGroup.uiHeading || componentGroup.internalName;
              }
            }

            const variantTitle = variant ? variant.title : variantId;
            // Filter out "Default Title" as it's not useful for single variants
            const cleanVariantTitle = variantTitle === 'Default Title' ? '' : variantTitle;
            return groupPrefix && cleanVariantTitle
              ? `${groupPrefix} / ${cleanVariantTitle}`
              : groupPrefix || cleanVariantTitle;
          })
          .filter(Boolean);

        return variantTitles.join('<br>');
      }

      // Handle single values
      const variant = variants.get(selectedValue);
      if (variant) {
        // Get component group info if available
        let groupPrefix = '';
        if (variant.componentGroupId) {
          const componentGroup = componentGroups.get(variant.componentGroupId);
          if (componentGroup) {
            groupPrefix = componentGroup.uiHeading || componentGroup.internalName;
          }
        }

        const variantTitle = variant.title;
        // Filter out "Default Title" as it's not useful for single variants
        const cleanVariantTitle = variantTitle === 'Default Title' ? '' : variantTitle;
        return groupPrefix && cleanVariantTitle
          ? `${groupPrefix} / ${cleanVariantTitle}`
          : groupPrefix || cleanVariantTitle;
      }
      return String(selectedValue);
    }

    return String(selectedValue);
  };

  /**
   * Gets the price for a slot selection, applying price modifiers from component groups.
   * @param {string|string[]} selectedValue - The selected variant ID(s).
   * @returns {number} - The price in pounds.
   */
  const getSlotPrice = (selectedValue) => {
    if (!selectedValue) return 0;

    // Handle arrays for grouped products.
    if (Array.isArray(selectedValue)) {
      return selectedValue.reduce((total, variantId) => {
        const variant = variants.get(variantId);
        if (!variant || !variant.price) return total;

        // Find the component group that contains this variant to get price modifier.
        let priceModifierType = null;
        let priceModifierValue = null;

        for (const [, componentGroup] of componentGroups.entries()) {
          if (componentGroup.productVariantIds?.includes(variantId)) {
            priceModifierType = componentGroup.priceModifierType;
            priceModifierValue = componentGroup.priceModifierValue;
            break;
          }
        }

        // Calculate the final price with modifier applied.
        const originalPrice =
          typeof variant.price.amount === 'string' ? Number(variant.price.amount) : variant.price.amount;
        const finalPrice = calculateFinalPriceModifier({
          priceModifierType,
          priceModifierValue,
          variantPrice: originalPrice
        });

        return total + finalPrice;
      }, 0);
    }

    // Handle single values.
    const variant = variants.get(selectedValue);
    if (!variant || !variant.price) return 0;

    // Find the component group that contains this variant to get price modifier.
    let priceModifierType = null;
    let priceModifierValue = null;

    for (const [, componentGroup] of componentGroups.entries()) {
      if (componentGroup.productVariantIds?.includes(selectedValue)) {
        priceModifierType = componentGroup.priceModifierType;
        priceModifierValue = componentGroup.priceModifierValue;
        break;
      }
    }

    // Calculate the final price with modifier applied.
    const originalPrice =
      typeof variant.price.amount === 'string' ? Number(variant.price.amount) : variant.price.amount;
    const finalPrice = calculateFinalPriceModifier({
      priceModifierType,
      priceModifierValue,
      variantPrice: originalPrice
    });

    return finalPrice;
  };

  /**
   * Navigates to the step containing the specified slot.
   * @param {string} slotId - The ID of the slot to navigate to.
   */
  const navigateToSlot = (slotId) => {
    if (!slotId || !setCurrentStepId) return;

    const stepId = findStepForSlot(slotId);
    if (stepId) {
      setCurrentStepId(stepId);
    }
  };

  /**
   * Handles the edit button click for a summary item.
   * @param {string} slotId - The ID of the slot to navigate to.
   */
  const handleEditClick = (slotId) => {
    navigateToSlot(slotId);
    // Close the summary modal after navigation.
    if (onClose) {
      onClose();
    }
  };

  /**
   * Creates summary items from all available slots, showing both selected and unselected items.
   * Orders slots according to Map insertion order to match the original personalisation flow.
   * This ensures the summary matches the order users see in the personalisation flow.
   * @returns {Array} - Array of summary items with slot info, selections, and prices.
   */
  const getSummaryItems = () => {
    if (!personalisationSlots || !personalisationSteps) return [];

    // Get all steps in Map insertion order to match the personalisation flow
    const orderedSteps = Array.from(personalisationSteps.entries());

    // Collect all slots in the order they appear in steps
    const orderedSlots = [];

    orderedSteps.forEach(([stepId, step]) => {
      if (step.slots && Array.isArray(step.slots)) {
        step.slots.forEach((slotId) => {
          const slotData = personalisationSlots.get(slotId);
          if (slotData) {
            // Find the selection for this slot
            const selection = selections.slots?.find((slot) => slot.id === slotId);
            const isSelected = selection?.selected !== null && selection?.selected !== '';
            const displayValue =
              isSelected && selection?.selected ? getSlotDisplayValue(slotData, selection.selected) : '';
            const price = isSelected && selection?.selected ? getSlotPrice(selection.selected) : 0;

            orderedSlots.push({
              id: slotData.id,
              stepId,
              title: slotData.summaryLabel || slotData.uiHeading || 'Personalisation',
              selectedValue: displayValue,
              price,
              slotType: slotData.slotType,
              isSelected,
              overridePrice: slotData.overridePrice,
              isFreeSlot: slotData.isFreeSlot
            });
          }
        });
      }
    });

    return orderedSlots;
  };

  const productTitle = product?.title || 'Summary';
  const summaryItems = getSummaryItems();

  return {
    productTitle,
    summaryItems,
    currentTotalPrice,
    navigateToSlot,
    handleEditClick,
    formatPrice
  };
}
